/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {resetMyReportsSelection} from '@ion/applications/src/pages/analytics/store';
import {info} from '@ion/core/src/logger/logger';
import {getAccount} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import GroupProperties, {getWebMapId} from '@ion/core/src/services/GroupProperties';
import Sisense from '@ion/core/src/services/Sisense';
import {constructDriverScores} from '@ion/core/src/services/SisenseUtils';
import {Account, AccountLink} from '@ion/core/src/types/entities';
import assetLoader from '@ion/dls/src/selectiontree/utils/AssetTreeLoader';
import {getScoresParams} from '@/routes/reports/reportsUtils';
import changePage, {getRoute, Route} from '@/routes/RouteDefinitions';
import Features, {hasAppNotificationAccess} from '@/security/Features';
import {resetFilterTreeNodes, resetTreeNodes} from '@/store/actions/groups-filter';
import {setPage} from '@/store/actions/nav';
import {
  activateRefresh,
  deactivateRefresh,
  deselectAllNotifications,
  resetNotifications
} from '@/store/actions/notifications';
import {
  resetTracking,
  setGridFilteredAssetIds,
  setMapviewExtent,
  setMapviewInitialExtent
} from '@/store/actions/tracking';
import {getDefaultRoute} from '../../routes/RouteSecurity';
import {createAncestorList} from '../../store/helpers';
import {ReportsMode, ReportTabMode} from '../../types/redux';
import {selectSchedule} from './maintenance';
import {clearFilters, retrieveReportOptions, setSisenseDefaultDashboard} from './reports';
import {refreshTrackingPage} from './tracking';

export const TOGGLE_GLOBAL_GROUP_CHOOSER = 'TOGGLE_GLOBAL_GROUP_CHOOSER';
export const SET_GLOBAL_GROUP            = 'SET_GLOBAL_GROUP';
export const GLOBAL_GROUP_INITIALIZED    = 'GLOBAL_GROUP_INITIALIZED';
export const SET_LOADING_STATE           = 'SET_LOADING_STATE';
export const SET_CURRENT_ACCOUNT         = 'SET_CURRENT_ACCOUNT';
export const SET_CURRENT_WEBMAPID        = 'SET_CURRENT_WEBMAPID';
export const REFRESH_ACCOUNT_HIERARCHY   = 'REFRESH_ACCOUNT_HIERARCHY';

export const setWebMapId = (webMapId: string) => ({
  type : SET_CURRENT_WEBMAPID, webMapId
});

export const showChangeGroup = (value: boolean) => ({
  type : TOGGLE_GLOBAL_GROUP_CHOOSER, value
});

/**
 * @setGlobalGroup
 */
export const setGlobalGroup = (group: AccountLink) => {
  return async(dispatch, getState) => {
    const {
      login: {
        user
      },
      globalGroup: {
        globalGroupId
      },
      nav: {
        currentPage
      }
    } = getState();
    dispatch({type: SET_LOADING_STATE, group});
    if ([Route.TRACKING, Route.TRACKING2].includes(currentPage?.slice(1, currentPage?.length))) {
      dispatch(refreshTrackingPage());
    } else {
      dispatch(resetTracking());
    }
    // Resetting the Filter Group Tree Nodes states when selected group changes
    if (globalGroupId !== entityId(group)) {
      assetLoader?.reset();
      dispatch(resetFilterTreeNodes({nodes: null}));
      dispatch(setMapviewExtent(null));
      dispatch(setMapviewInitialExtent(null));
      dispatch(setGridFilteredAssetIds(null) as any);
      dispatch(setSisenseDefaultDashboard(null));
      dispatch(selectSchedule(null));
      dispatch(deselectAllNotifications());
    }
    const groupId = entityId(group);
    Sisense.setAccountId(groupId);
    const account = await getAccount(entityId(group));
    //Get group's inherited properties
    await GroupProperties.getInheritedProperties(groupId);
    resetMyReportsSelection();
    Sisense.updateSettings({
      ...constructDriverScores(getScoresParams({account} as any))
    });
    const {isRestricted, restrictedChanged} = Features.setCurrentAccount(group);
    const ancestorAccountId                 = await createAncestorList(groupId);
    dispatch({type: SET_CURRENT_WEBMAPID, webMapId: getWebMapId()});
    dispatch({type: SET_GLOBAL_GROUP, group, ancestorAccountId, user});
    dispatch({type: SET_CURRENT_ACCOUNT, account});
    dispatch(retrieveReportOptions());
    postSetGlobalGroup({dispatch, getState, restrictedChanged, isRestricted});
  };
};

export const setCurrentAccount = (account: Account) => ({type: SET_CURRENT_ACCOUNT, account});

/**
 * @private
 */
const postSetGlobalGroup = ({dispatch, getState, restrictedChanged, isRestricted}) => {
  // reset notification content based on the global group that user is selected.
  dispatch(resetNotifications());
  dispatch(clearFilters());
  dispatch(clearFilters(ReportsMode.ShipmentHistory));
  dispatch(resetTreeNodes({mode: ReportTabMode.ShipmentHistory}));
  const route = getState().nav.currentPage;
  if (restrictedChanged) {
    if (isRestricted) {
      const restrictedRoute = getDefaultRoute(route);
      dispatch(deactivateRefresh());
      info('page changing to:', route);
      dispatch(setPage(getRoute(restrictedRoute)));
      changePage(restrictedRoute, '', false);
    } else {
      dispatch(setPage(getRoute(route)));
      info('page changing to:', route);
      changePage(route, '', false);
      if (hasAppNotificationAccess()) {
        dispatch(activateRefresh());
      }
    }
  }
};

/**
 * Refresh the account hierarchy tree on add/edit group(s) ...
 */
export const refreshAccountHierarchy = (accounts: AccountLink[]) => {
  return {type: REFRESH_ACCOUNT_HIERARCHY, accounts};
};
